import { useCallback, useEffect, useState } from 'react';
import { of } from 'rxjs';
import { useObservableValue } from '../../../hooks';
import { AlertVariants } from '../../Alert';
import { AlertBanner } from '../../AlertBanner';
import { Button } from '../../Button';
import { HStack } from '../../Core';
import { FormControlSizes } from '../../Form';
import { Text } from '../../Text';
import type { UseBlotterTableProps } from './types';

/** Takes in an error observable and exposes the current error, as well as a way to dismiss the error */
export const useErrorDetail = (errorObservable: UseBlotterTableProps<unknown>['errorObservable']) => {
  const [shownError, setShownError] = useState<string | undefined>();
  const error = useObservableValue(() => errorObservable ?? of(undefined), [errorObservable]);

  useEffect(() => {
    setShownError(error?.error ? `An error occurred: ${error?.error?.msg}` : undefined);
  }, [error]);

  const handleDismissError = useCallback(() => {
    setShownError(undefined);
  }, []);

  return {
    shownError,
    dismissError: handleDismissError,
  };
};

export const BlotterErrorDetail = ({
  errorDetails,
  disableErrorBanner,
}: {
  errorDetails: ReturnType<typeof useErrorDetail> | undefined;
  disableErrorBanner: boolean | undefined;
}) => {
  if (disableErrorBanner) {
    return null;
  }

  if (errorDetails?.shownError == null) {
    return null;
  }

  return (
    <AlertBanner variant={AlertVariants.Negative}>
      <HStack gap="spacing6" color="colorTextImportant" data-testid="blotter-error-banner">
        <Text>{errorDetails.shownError}</Text>
        <Button
          size={FormControlSizes.Tiny}
          onClick={() => errorDetails.dismissError()}
          data-testid="blotter-error-banner-dismiss-button"
        >
          Dismiss
        </Button>
      </HStack>
    </AlertBanner>
  );
};
